import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import {
  Button,
  Card,
  CardBody,
  Chip,
  Radio,
  RadioGroup,
  Select,
  SelectItem,
  Spinner,
} from "@heroui/react";
import {
  CreditCard,
  Clock,
  FileText,
  Wallet,
  AlertTriangle,
  Plus,
} from "lucide-react";
import { AddPaymentMethodDialog } from "@/features/profile/add-payment-method.dialog";
import { pickupDisplayCents } from "../pickup-pricing";
import { PaymentMethodType } from "@/api.types.v2";
import {
  usePaymentMethods,
  useMyPaymentTerms,
} from "@/api/use-payment-methods-api";
import { useCreditBalance } from "@/api/use-payment-options-api";
import type { PaymentMethod } from "@/api.types";
import { useSession } from "@/services/auth";
import type { OrderDraft, PaymentChoice } from "../types";
import { useTranslation } from "react-i18next";

// Card brand logo helper
const getCardBrandLogo = (brand?: string): string => {
  const baseUrl =
    "https://raw.githubusercontent.com/datatrans/payment-logos/master/assets/cards";
  switch (brand?.toLowerCase()) {
    case "mastercard":
      return `${baseUrl}/mastercard.svg`;
    case "visa":
      return `${baseUrl}/visa.svg`;
    case "amex":
    case "american express":
      return `${baseUrl}/american-express.svg`;
    case "diners":
    case "diners club":
      return `${baseUrl}/diners.svg`;
    case "discover":
      return `${baseUrl}/discover.svg`;
    case "jcb":
      return `${baseUrl}/jcb.svg`;
    case "unionpay":
      return `${baseUrl}/unionpay.svg`;
    default:
      return `${baseUrl}/card-generic.svg`;
  }
};

// A card is expired once its expiry month has fully passed. Missing expiry
// data is treated as NOT expired — we don't have proof, so let Stripe be the
// authority rather than block a usable card on absent metadata.
const isCardExpired = (pm: PaymentMethod): boolean => {
  if (!pm.expiryYear || !pm.expiryMonth) return false;
  const now = new Date();
  const curYear = now.getFullYear();
  const curMonth = now.getMonth() + 1; // 1-12
  return (
    pm.expiryYear < curYear ||
    (pm.expiryYear === curYear && pm.expiryMonth < curMonth)
  );
};

export interface PaymentStepHandle {
  submit: () => void;
}

export interface PaymentSelectionStepProps {
  data: OrderDraft;
  onNext: (data: Partial<OrderDraft>) => void;
  /** Reports the submit button's label (varies by payment choice). */
  onActionLabelChange?: (label: string) => void;
  /** Reports whether submit is enabled. */
  onCanSubmitChange?: (canSubmit: boolean) => void;
}

interface OptionCardProps {
  selected: boolean;
  disabled?: boolean;
  onSelect?: () => void;
  className?: string;
  children: React.ReactNode;
}

function OptionCard({
  selected,
  disabled,
  onSelect,
  className,
  children,
}: OptionCardProps) {
  const base = `transition-all w-full ${className ?? ""}`;
  if (disabled) {
    return (
      <Card shadow="none" className={`${base} cursor-not-allowed opacity-60`}>
        <CardBody className="p-4">{children}</CardBody>
      </Card>
    );
  }
  return (
    <Card
      shadow="none"
      isPressable
      onPress={onSelect}
      className={`${base} ${
        selected
          ? "bg-primary-50 ring-2 ring-inset ring-primary shadow-medium"
          : "bg-content1 shadow-small hover:shadow-medium"
      }`}
    >
      <CardBody className="p-4">{children}</CardBody>
    </Card>
  );
}

export const PaymentSelectionStep = forwardRef<
  PaymentStepHandle,
  PaymentSelectionStepProps
>(function PaymentSelectionStep(
  { data, onNext, onActionLabelChange, onCanSubmitChange },
  ref
) {
  const { t } = useTranslation("app");
  const { customer } = useSession();
  const isTestAccount = customer?.isTestAccount ?? false;

  const { data: billingData, isLoading: isLoadingPaymentMethods } =
    usePaymentMethods();
  const { data: paymentTermsData, isLoading: isLoadingPaymentTerms } =
    useMyPaymentTerms();
  const { data: creditBalanceData, isLoading: isLoadingCreditBalance } =
    useCreditBalance();

  const allPaymentMethods = billingData?.paymentMethods ?? [];
  // Only non-expired cards can actually be charged — auto-select and the
  // submit gate both work off this set so we never land on a dead card.
  const validPaymentMethods = allPaymentMethods.filter(
    (pm) => !isCardExpired(pm)
  );
  const hasValidPaymentMethod = validPaymentMethods.length > 0;
  // Prefer the customer's default when it's still valid, else the first
  // usable card. `isDefault` alone isn't enough — the default can be expired.
  const autoSelectMethod =
    validPaymentMethods.find((pm) => pm.isDefault) ??
    validPaymentMethods[0] ??
    undefined;

  const hasNetTermsAccess = paymentTermsData?.hasNetTermsAccess ?? false;
  const availableCredit = paymentTermsData?.availableCredit ?? 0;
  const paymentTerms = paymentTermsData?.paymentTerms ?? [];
  const hasUnpaidInvoices = paymentTermsData?.hasUnpaidInvoices ?? false;
  const unpaidInvoiceCount = paymentTermsData?.unpaidInvoiceCount ?? 0;

  const [paymentChoice, setPaymentChoice] = useState<PaymentChoice | null>(
    data.paymentChoice ?? (autoSelectMethod ? "SAVED_CARD" : "DECIDE_LATER")
  );

  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState<
    string | null
  >(data.selectedPaymentMethodId ?? autoSelectMethod?.id ?? null);

  const [isAddCardOpen, setIsAddCardOpen] = useState(false);

  const [selectedPaymentTermsId, setSelectedPaymentTermsId] = useState<
    string | null
  >(data.selectedPaymentTermsId ?? null);

  const effectivePaymentTermsId =
    selectedPaymentTermsId ?? (paymentTerms[0]?.id || null);

  const creditBalanceCents = creditBalanceData?.balanceCents ?? 0;
  // Pull tax from the quote so the charge shown here matches Stripe's run.
  const labelPriceCents = data.selectedQuote?.labelPriceCents ?? 0;
  const labelTaxCents = data.selectedQuote?.taxCents ?? 0;
  const pickupTier =
    data.includePickup && data.pickup
      ? data.selectedQuote?.pickupOptions.find(
          (t) => t.pickupServiceId === data.pickup?.pickupServiceId
        )
      : undefined;
  const pickupDisplay = pickupDisplayCents(
    pickupTier,
    data.pickupBundleDiscountCents ?? 0,
    !!data.includePickup
  );
  const subtotalCents =
    labelPriceCents + pickupDisplay.pickupCents - pickupDisplay.discountCents;
  const taxCents = labelTaxCents + pickupDisplay.taxCents;
  const totalCost = subtotalCents + taxCents;

  const creditFrozen = creditBalanceData?.frozen ?? false;
  // Credit is full-coverage-only server-side, and a frozen wallet (open
  // deposit dispute) rejects all spends — don't offer what would 402/409.
  const canUseCredit = !creditFrozen && creditBalanceCents >= totalCost;
  const creditCoversPartially =
    !creditFrozen && creditBalanceCents > 0 && creditBalanceCents < totalCost;

  const handleContinue = useCallback(() => {
    if (paymentChoice === "CREDIT_ONLY") {
      onNext({
        paymentMethodType: PaymentMethodType.CREDIT,
        paymentChoice: "CREDIT_ONLY",
      });
    } else if (paymentChoice === "SAVED_CARD" && selectedPaymentMethodId) {
      onNext({
        paymentMethodType: PaymentMethodType.SAVED_CARD,
        paymentChoice: "SAVED_CARD",
        selectedPaymentMethodId,
      });
    } else if (paymentChoice === "DECIDE_LATER") {
      onNext({
        paymentMethodType: PaymentMethodType.PAYMENT_LINK,
        paymentChoice: "DECIDE_LATER",
      });
    } else if (paymentChoice === "INVOICE" && effectivePaymentTermsId) {
      onNext({
        paymentMethodType: PaymentMethodType.INVOICE,
        paymentChoice: "INVOICE",
        paymentTermsId: effectivePaymentTermsId,
      });
    }
  }, [paymentChoice, selectedPaymentMethodId, effectivePaymentTermsId, onNext]);

  // A restored draft can carry CREDIT_ONLY from before the wallet froze or
  // the total rose past the balance — clear it so the hidden option can't
  // submit (the guard below also blocks it while the balance loads).
  useEffect(() => {
    if (
      paymentChoice === "CREDIT_ONLY" &&
      !canUseCredit &&
      !isLoadingCreditBalance
    ) {
      setPaymentChoice(null);
    }
  }, [paymentChoice, canUseCredit, isLoadingCreditBalance]);

  // Keep the saved-card selection pointed at a usable card: clears a stale or
  // expired pick and adopts a newly-added card once the list refreshes.
  useEffect(() => {
    if (paymentChoice !== "SAVED_CARD") return;
    const selectionValid =
      !!selectedPaymentMethodId &&
      validPaymentMethods.some((pm) => pm.id === selectedPaymentMethodId);
    if (!selectionValid) {
      setSelectedPaymentMethodId(autoSelectMethod?.id ?? null);
    }
  }, [
    paymentChoice,
    selectedPaymentMethodId,
    validPaymentMethods,
    autoSelectMethod,
  ]);

  // SAVED_CARD is only submittable against a non-expired card — a stale draft
  // or expired default must not enable the charge button.
  const selectedCardValid =
    !!selectedPaymentMethodId &&
    validPaymentMethods.some((pm) => pm.id === selectedPaymentMethodId);
  const canSubmit =
    !!paymentChoice &&
    !(paymentChoice === "CREDIT_ONLY" && !canUseCredit) &&
    !(paymentChoice === "SAVED_CARD" && !selectedCardValid) &&
    !(paymentChoice === "INVOICE" && !effectivePaymentTermsId);
  const actionLabel =
    paymentChoice === "CREDIT_ONLY"
      ? t("createShipment.payWithCredit")
      : paymentChoice === "SAVED_CARD"
        ? t("createShipment.createAndPay")
        : t("createShipment.createOrder");

  useEffect(() => {
    onCanSubmitChange?.(canSubmit);
  }, [canSubmit, onCanSubmitChange]);

  useEffect(() => {
    onActionLabelChange?.(actionLabel);
  }, [actionLabel, onActionLabelChange]);

  useImperativeHandle(ref, () => ({ submit: handleContinue }), [
    handleContinue,
  ]);

  const formatCurrency = (cents: number) => {
    return new Intl.NumberFormat("en-CA", {
      style: "currency",
      currency: "CAD",
    }).format(cents / 100);
  };

  if (
    isLoadingPaymentMethods ||
    isLoadingPaymentTerms ||
    isLoadingCreditBalance
  ) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" color="primary" />
      </div>
    );
  }

  const creditLimit = paymentTermsData?.creditLimit ?? 0;
  const outstanding = paymentTermsData?.outstandingBalance ?? 0;
  const utilization =
    creditLimit > 0 ? (outstanding + totalCost) / creditLimit : 0;
  const showCreditWarning =
    !isTestAccount &&
    hasNetTermsAccess &&
    !!paymentTermsData &&
    utilization >= 0.8;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl md:text-2xl font-bold tracking-tight">
          {t("createShipment.selectPaymentMethod")}
        </h2>
        <p className="text-small text-default-500">
          {t("createShipment.paymentMethodLede")}
        </p>
      </div>

      {showCreditWarning && (
        <Card shadow="none" className="border border-warning-200 bg-warning-50">
          <CardBody className="p-4 flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-between">
            <div className="flex items-start sm:items-center gap-3">
              <AlertTriangle className="h-5 w-5 text-warning-600 shrink-0 mt-0.5 sm:mt-0" />
              <div>
                <p className="text-sm font-medium text-warning-700">
                  {t("createShipment.creditLimitWarning", {
                    percent: Math.round(utilization * 100),
                    limit: formatCurrency(creditLimit),
                  })}
                </p>
                <p className="text-tiny text-warning-700/80">
                  {t("createShipment.creditLimitWarningBody")}
                </p>
              </div>
            </div>
            <Button
              variant="flat"
              color="warning"
              size="sm"
              className="w-full sm:w-auto"
              onPress={() => window.open("/invoices", "_blank")}
            >
              {t("createShipment.viewInvoices")}
            </Button>
          </CardBody>
        </Card>
      )}

      <RadioGroup
        value={paymentChoice ?? ""}
        onValueChange={(value) => setPaymentChoice(value as PaymentChoice)}
        classNames={{ wrapper: "space-y-3" }}
      >
        {canUseCredit && (
          <OptionCard
            selected={paymentChoice === "CREDIT_ONLY"}
            onSelect={() => setPaymentChoice("CREDIT_ONLY")}
          >
            <div className="flex items-start gap-4">
              <Radio value="CREDIT_ONLY" classNames={{ wrapper: "mt-1" }}>
                <span className="sr-only">
                  {t("createShipment.payWithCreditBalance")}
                </span>
              </Radio>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <Wallet className="h-5 w-5 text-success-600" />
                  <span className="text-sm md:text-base font-medium">
                    {t("createShipment.payWithCreditBalance")}
                  </span>
                  <Chip size="sm" variant="flat" color="success">
                    {t("createShipment.instant")}
                  </Chip>
                </div>
                <p className="text-tiny md:text-small text-default-500">
                  {t("createShipment.creditBalanceUse", {
                    amount: formatCurrency(creditBalanceCents),
                  })}
                </p>
              </div>
            </div>
          </OptionCard>
        )}

        {creditCoversPartially && (
          <Card
            shadow="none"
            className="border border-default-200 bg-default-50"
          >
            <CardBody className="p-4 flex flex-row items-start gap-3">
              <Wallet className="h-5 w-5 text-default-400 shrink-0 mt-0.5" />
              <p className="text-tiny md:text-small text-default-500">
                {t("createShipment.creditPartialBody", {
                  balance: formatCurrency(creditBalanceCents),
                  total: formatCurrency(totalCost),
                })}
              </p>
            </CardBody>
          </Card>
        )}

        {creditFrozen && creditBalanceCents > 0 && (
          <Card
            shadow="none"
            className="border border-warning-200 bg-warning-50"
          >
            <CardBody className="p-4 flex flex-row items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-warning-600 shrink-0 mt-0.5" />
              <p className="text-tiny md:text-small text-warning-700">
                {t("createShipment.creditFrozenBodyShort")}
              </p>
            </CardBody>
          </Card>
        )}

        {!isTestAccount && (
          <OptionCard
            selected={paymentChoice === "SAVED_CARD"}
            onSelect={() => {
              setPaymentChoice("SAVED_CARD");
              if (!selectedPaymentMethodId && autoSelectMethod) {
                setSelectedPaymentMethodId(autoSelectMethod.id);
              }
            }}
          >
            <div className="flex items-start gap-4">
              <Radio value="SAVED_CARD" classNames={{ wrapper: "mt-1" }}>
                <span className="sr-only">
                  {t("createShipment.payNowSavedCard")}
                </span>
              </Radio>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <CreditCard className="h-5 w-5 text-primary" />
                  <span className="text-sm md:text-base font-medium">
                    {t("createShipment.payNowSavedCard")}
                  </span>
                  <Chip size="sm" variant="flat" color="success">
                    {t("createShipment.instant")}
                  </Chip>
                </div>
                <p className="text-tiny md:text-small text-default-500 mb-3">
                  {t("createShipment.cardChargedImmediate")}
                </p>

                {paymentChoice === "SAVED_CARD" && (
                  <div className="space-y-2 mt-3 border-t border-divider pt-3">
                    {allPaymentMethods.map((method: PaymentMethod) => {
                      const expired = isCardExpired(method);
                      const isSelected = selectedPaymentMethodId === method.id;
                      return (
                        <div
                          key={method.id}
                          role="button"
                          tabIndex={expired ? -1 : 0}
                          aria-disabled={expired}
                          aria-pressed={isSelected}
                          className={`flex items-center gap-3 p-3 rounded-medium border transition-all ${
                            expired
                              ? "border-divider opacity-60 cursor-not-allowed"
                              : isSelected
                                ? "border-primary bg-primary/5 cursor-pointer"
                                : "border-divider hover:border-default-300 cursor-pointer"
                          }`}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (expired) return;
                            setSelectedPaymentMethodId(method.id);
                          }}
                          onKeyDown={(e) => {
                            if (expired) return;
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              e.stopPropagation();
                              setSelectedPaymentMethodId(method.id);
                            }
                          }}
                        >
                          <img
                            src={getCardBrandLogo(method.brand)}
                            alt={method.brand ?? t("settings.cardAlt")}
                            className="h-6 w-auto"
                          />
                          <div className="flex-1">
                            <span className="text-sm font-medium">
                              {method.brand?.charAt(0).toUpperCase()}
                              {method.brand?.slice(1).toLowerCase()} ••••{" "}
                              {method.last4}
                            </span>
                            {expired && (
                              <Chip
                                size="sm"
                                variant="flat"
                                color="danger"
                                className="ml-2 text-tiny"
                              >
                                {t("createShipment.cardExpired")}
                              </Chip>
                            )}
                            {!expired && method.isDefault && (
                              <Chip
                                size="sm"
                                variant="bordered"
                                className="ml-2 text-tiny"
                              >
                                {t("settings.default")}
                              </Chip>
                            )}
                          </div>
                          {isSelected && !expired && (
                            <div className="h-4 w-4 rounded-full bg-primary flex items-center justify-center">
                              <div className="h-2 w-2 rounded-full bg-white" />
                            </div>
                          )}
                        </div>
                      );
                    })}

                    {!hasValidPaymentMethod && (
                      <div className="flex items-start gap-2 rounded-medium border border-danger-200 bg-danger-50 px-3 py-2.5">
                        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-danger" />
                        <span className="text-tiny text-danger-700">
                          {t("createShipment.noUsableCard")}
                        </span>
                      </div>
                    )}

                    <Button
                      variant="flat"
                      size="sm"
                      color="primary"
                      startContent={<Plus className="h-4 w-4" />}
                      onPress={() => setIsAddCardOpen(true)}
                    >
                      {t("createShipment.addCard")}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </OptionCard>
        )}

        {!isTestAccount && (
          <OptionCard
            selected={paymentChoice === "DECIDE_LATER"}
            onSelect={() => setPaymentChoice("DECIDE_LATER")}
          >
            <div className="flex items-start gap-4">
              <Radio value="DECIDE_LATER" classNames={{ wrapper: "mt-1" }}>
                <span className="sr-only">
                  {t("createShipment.decideLater")}
                </span>
              </Radio>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <Clock className="h-5 w-5 text-primary" />
                  <span className="text-sm md:text-base font-medium">
                    {t("createShipment.decideLater")}
                  </span>
                </div>
                <p className="text-tiny md:text-small text-default-500">
                  {t("createShipment.decideLaterBody")}
                </p>
              </div>
            </div>
          </OptionCard>
        )}

        {!isTestAccount && hasNetTermsAccess && !hasUnpaidInvoices && (
          <OptionCard
            selected={paymentChoice === "INVOICE"}
            onSelect={() => setPaymentChoice("INVOICE")}
          >
            <div className="flex items-start gap-4">
              <Radio value="INVOICE" classNames={{ wrapper: "mt-1" }}>
                <span className="sr-only">
                  {t("createShipment.invoiceNetTerms")}
                </span>
              </Radio>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <FileText className="h-5 w-5 text-primary" />
                  <span className="text-sm md:text-base font-medium">
                    {t("createShipment.invoiceNetTerms")}
                  </span>
                  <Chip size="sm" variant="flat" color="primary">
                    {t("createShipment.available")}
                  </Chip>
                </div>
                <p className="text-tiny md:text-small text-default-500 mb-1">
                  {t("createShipment.payWithinDays", {
                    days: paymentTerms[0]?.paymentDueDays || 30,
                  })}
                </p>
                <p className="text-tiny text-default-500">
                  {t("createShipment.availableCredit", {
                    amount: formatCurrency(availableCredit),
                  })}
                </p>

                {paymentChoice === "INVOICE" && paymentTerms.length > 1 && (
                  <div className="mt-3 border-t border-divider pt-3">
                    <Select
                      size="sm"
                      variant="bordered"
                      label={t("createShipment.paymentTermsLabel")}
                      labelPlacement="outside"
                      placeholder={t("createShipment.selectTerms")}
                      selectedKeys={
                        effectivePaymentTermsId ? [effectivePaymentTermsId] : []
                      }
                      onSelectionChange={(keys) => {
                        const v = Array.from(keys)[0];
                        if (typeof v === "string") setSelectedPaymentTermsId(v);
                      }}
                      className="w-full"
                    >
                      {paymentTerms.map((term) => (
                        <SelectItem key={term.id}>{term.name}</SelectItem>
                      ))}
                    </Select>
                  </div>
                )}
              </div>
            </div>
          </OptionCard>
        )}

        {!isTestAccount && hasNetTermsAccess && hasUnpaidInvoices && (
          <OptionCard
            selected={false}
            disabled
            className="border border-warning-200"
          >
            <div className="flex items-start gap-4">
              <Radio
                value="INVOICE"
                isDisabled
                classNames={{ wrapper: "mt-1" }}
              >
                <span className="sr-only">
                  {t("createShipment.invoiceNetTerms")}
                </span>
              </Radio>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <FileText className="h-5 w-5 text-warning-600" />
                  <span className="text-sm md:text-base font-medium text-default-500">
                    {t("createShipment.invoiceNetTerms")}
                  </span>
                  <Chip size="sm" variant="flat" color="warning">
                    {t("createShipment.paymentRequired")}
                  </Chip>
                </div>
                <p className="text-tiny md:text-small text-warning-700 mb-3">
                  {t("createShipment.unpaidInvoicesBody", {
                    count: unpaidInvoiceCount,
                  })}
                </p>
                <Button
                  variant="flat"
                  color="warning"
                  size="sm"
                  onPress={() => window.open("/invoices", "_blank")}
                >
                  {t("createShipment.payInvoice", {
                    count: unpaidInvoiceCount,
                  })}
                </Button>
              </div>
            </div>
          </OptionCard>
        )}

        {!isTestAccount && !hasNetTermsAccess && (
          <OptionCard selected={false} disabled className="opacity-50">
            <div className="flex items-start gap-4">
              <Radio
                value="INVOICE"
                isDisabled
                classNames={{ wrapper: "mt-1" }}
              >
                <span className="sr-only">
                  {t("createShipment.invoiceNetTerms")}
                </span>
              </Radio>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <FileText className="h-5 w-5 text-default-500" />
                  <span className="text-sm md:text-base font-medium text-default-500">
                    {t("createShipment.invoiceNetTerms")}
                  </span>
                  <Chip size="sm" variant="flat" color="default">
                    {t("createShipment.businessOnly")}
                  </Chip>
                </div>
                <p className="text-tiny md:text-small text-default-500">
                  {t("createShipment.businessOnlyBody")}
                </p>
              </div>
            </div>
          </OptionCard>
        )}
      </RadioGroup>

      {paymentChoice === "SAVED_CARD" && selectedPaymentMethodId && (
        <Card shadow="none" className="border border-success-200 bg-success-50">
          <CardBody className="py-4">
            <p className="text-tiny md:text-small text-success-700">
              {t("createShipment.cardChargeNotice", {
                amount: formatCurrency(totalCost),
                action: t("createShipment.createAndPay"),
              })}
            </p>
          </CardBody>
        </Card>
      )}

      {paymentChoice === "CREDIT_ONLY" && (
        <Card shadow="none" className="border border-success-200 bg-success-50">
          <CardBody className="py-4">
            <p className="text-tiny md:text-small text-success-700">
              {t("createShipment.creditDeductNotice", {
                amount: formatCurrency(totalCost),
              })}
            </p>
          </CardBody>
        </Card>
      )}

      {paymentChoice === "DECIDE_LATER" && (
        <Card shadow="none" className="border border-warning-200 bg-warning-50">
          <CardBody className="py-4">
            <p className="text-tiny md:text-small text-warning-700">
              {t("createShipment.decideLaterNotice")}
            </p>
          </CardBody>
        </Card>
      )}

      {paymentChoice === "INVOICE" && effectivePaymentTermsId && (
        <Card shadow="none" className="border border-primary/20 bg-primary/5">
          <CardBody className="py-4">
            <p className="text-tiny md:text-small text-primary">
              {t("createShipment.invoiceNotice", {
                days:
                  paymentTerms.find((t) => t.id === effectivePaymentTermsId)
                    ?.paymentDueDays ?? 30,
              })}
            </p>
          </CardBody>
        </Card>
      )}

      <AddPaymentMethodDialog
        open={isAddCardOpen}
        onOpenChange={setIsAddCardOpen}
      />
    </div>
  );
});
